import { useEffect, useRef, useState, useCallback } from "react";
import { useMap, useMapEvents, Polygon, Polyline, CircleMarker } from "react-leaflet";
import L from "leaflet";
import type { LatLng } from "@/contexts/GameContext";

// ─── 面積計算（Shoelace + Haversine補正）────────────────────────
export function calcAreaKm2(pts: LatLng[]): number {
  if (pts.length < 3) return 0;
  const R = 6371; // km
  let area = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const lat1 = (pts[i].lat * Math.PI) / 180;
    const lat2 = (pts[j].lat * Math.PI) / 180;
    const dLng = ((pts[j].lng - pts[i].lng) * Math.PI) / 180;
    area += (dLng * (2 + Math.sin(lat1) + Math.sin(lat2)));
  }
  return Math.abs((area * R * R) / 2);
}

// ─── 直線スナップ補助（Shift押下時）────────────────────────────
function snapStraight(from: LatLng, to: LatLng): LatLng {
  const dLat = Math.abs(to.lat - from.lat);
  const dLng = Math.abs(to.lng - from.lng);
  // 45度判定: どちらが近いか
  if (dLat > dLng * 2) {
    // 縦方向にスナップ
    return { lat: to.lat, lng: from.lng };
  } else if (dLng > dLat * 2) {
    // 横方向にスナップ
    return { lat: from.lat, lng: to.lng };
  }
  // 45度斜めスナップ
  const d = Math.min(dLat, dLng);
  return {
    lat: from.lat + (to.lat > from.lat ? d : -d),
    lng: from.lng + (to.lng > from.lng ? d : -d),
  };
}

// ─── 始点への近接判定（ピクセル距離）────────────────────────────
function isNearStart(map: L.Map, a: LatLng, b: LatLng, thresholdPx = 18): boolean {
  const pa = map.latLngToContainerPoint([a.lat, a.lng]);
  const pb = map.latLngToContainerPoint([b.lat, b.lng]);
  return pa.distanceTo(pb) < thresholdPx;
}

interface Props {
  active: boolean;
  shiftHeld: boolean;
  onComplete: (polygon: LatLng[], areaKm2: number) => void;
  onCancel: () => void;
}

function DrawingLayer({ active, shiftHeld, onComplete, onCancel }: Props) {
  const map = useMap();
  const [points, setPoints] = useState<LatLng[]>([]);
  const [cursor, setCursor] = useState<LatLng | null>(null);
  const pointsRef = useRef<LatLng[]>([]);

  // pointsをrefと同期
  useEffect(() => { pointsRef.current = points; }, [points]);

  // 描画モード中はマップのドラッグを無効化
  useEffect(() => {
    if (active) {
      map.dragging.disable();
      map.getContainer().style.cursor = "crosshair";
    } else {
      map.dragging.enable();
      map.getContainer().style.cursor = "";
      setPoints([]);
      setCursor(null);
    }
    return () => {
      map.dragging.enable();
      map.getContainer().style.cursor = "";
    };
  }, [active, map]);

  // ESCキーでキャンセル
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onCancel(); setPoints([]); setCursor(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  useMapEvents({
    mousemove(e) {
      if (!active) return;
      const pts = pointsRef.current;
      let pos: LatLng = { lat: e.latlng.lat, lng: e.latlng.lng };
      if (shiftHeld && pts.length > 0) {
        pos = snapStraight(pts[pts.length - 1], pos);
      }
      setCursor(pos);
    },
    click(e) {
      if (!active) return;
      const pts = pointsRef.current;
      let pos: LatLng = { lat: e.latlng.lat, lng: e.latlng.lng };
      if (shiftHeld && pts.length > 0) {
        pos = snapStraight(pts[pts.length - 1], pos);
      }

      // 始点に近ければ閉じる（3点以上必要）
      if (pts.length >= 3 && isNearStart(map, pts[0], pos)) {
        const area = calcAreaKm2(pts);
        onComplete([...pts], area);
        setPoints([]);
        setCursor(null);
        return;
      }

      setPoints((prev) => [...prev, pos]);
    },
    dblclick(e) {
      if (!active) return;
      L.DomEvent.stop(e);
      const pts = pointsRef.current;
      if (pts.length >= 3) {
        const area = calcAreaKm2(pts);
        onComplete([...pts], area);
        setPoints([]);
        setCursor(null);
      }
    },
  });

  if (!active) return null;

  const allPts = cursor ? [...points, cursor] : points;
  const area = calcAreaKm2(points.length >= 3 ? points : []);
  const closeSnap = points.length >= 3 && cursor && isNearStart(map, points[0], cursor);

  return (
    <>
      {/* 描画中ポリゴン（塗り） */}
      {points.length >= 3 && (
        <Polygon
          positions={points.map((p) => [p.lat, p.lng])}
          pathOptions={{
            color: "#fbbf24",
            weight: 2,
            fillColor: "#fbbf24",
            fillOpacity: 0.15,
            dashArray: "6 4",
          }}
        />
      )}

      {/* カーソルまでのプレビューライン */}
      {allPts.length >= 2 && (
        <Polyline
          positions={allPts.map((p) => [p.lat, p.lng])}
          pathOptions={{
            color: closeSnap ? "#22c55e" : "#fbbf24",
            weight: 2.5,
            dashArray: closeSnap ? undefined : "8 5",
          }}
        />
      )}

      {/* 頂点マーカー */}
      {points.map((p, i) => (
        <CircleMarker
          key={i}
          center={[p.lat, p.lng]}
          radius={i === 0 ? 8 : 5}
          pathOptions={{
            color: i === 0 ? "#22c55e" : "#fbbf24",
            fillColor: i === 0 ? "#22c55e" : "#fbbf24",
            fillOpacity: 1,
            weight: 2,
          }}
        />
      ))}

      {/* カーソル位置マーカー */}
      {cursor && (
        <CircleMarker
          center={[cursor.lat, cursor.lng]}
          radius={4}
          pathOptions={{
            color: closeSnap ? "#22c55e" : "#fbbf24",
            fillColor: closeSnap ? "#22c55e" : "#fbbf24",
            fillOpacity: 0.8,
            weight: 1.5,
          }}
        />
      )}

      {/* 面積ヒント（右下） */}
      {points.length >= 2 && cursor && (
        <div
          style={{
            position: "absolute",
            bottom: 80,
            right: 16,
            zIndex: 1100,
            pointerEvents: "none",
          }}
        >
          <div className="bg-black/80 border border-white/10 rounded-xl px-3 py-2 text-xs text-white backdrop-blur-sm min-w-[160px]">
            <p className="text-gray-400 mb-1 text-xs font-medium">現在の面積</p>
            {/* 面積表示（m² / km² のみ） */}
            {area < 0.0001 ? (
              <p className="font-bold text-base text-red-400">
                {(area * 1_000_000).toFixed(0)} m²
              </p>
            ) : area < 1 ? (
              <p className="font-bold text-base text-emerald-400">
                {(area * 1_000_000).toFixed(0)} m²
              </p>
            ) : (
              <p className="font-bold text-base text-red-400">
                {area.toFixed(4)} km²
              </p>
            )}
            {area < 0.0001 && <p className="text-red-400 text-xs mt-0.5">⚠ 100 m²以上必要</p>}
            {area >= 1 && <p className="text-red-400 text-xs mt-0.5">⚠ 1 km²未満にしてください</p>}
            {area >= 0.0001 && area < 1 && <p className="text-emerald-400 text-xs mt-0.5">✓ 購入可能</p>}
            {closeSnap && <p className="text-emerald-400 text-xs mt-0.5 font-bold">● クリックで確定</p>}
          </div>
        </div>
      )}
    </>
  );
}

export default function PolygonDrawer({ active, shiftHeld, onComplete, onCancel }: Props) {
  return <DrawingLayer active={active} shiftHeld={shiftHeld} onComplete={onComplete} onCancel={onCancel} />;
}
